import React, { useCallback, useEffect, useMemo } from "react"
import { aws } from "@/services/aws"
import { cva } from "class-variance-authority"
import { Icons } from "@/components/ui/icons"
import useSWR from "swr"

import { InjectMapProps } from "@/types/leaflet"
import { minDelay } from "@/lib/timing"
import { cn } from "@/lib/utils"
import {
  AutocompleteCollection,
  AutocompleteGroup,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompletePopup,
  AutocompletePortal,
  AutocompletePositioner,
  AutocompleteRoot,
  AutocompleteStatus,
} from "@/components/ui/form.autocomplete"
import { appRoute } from "@/components/routes/app-route"
import { useSetAppSearch } from "@/components/routes/app-route.hooks"
import { AppPlace } from "@/components/routes/app-route.zod"

import { useLocationSuggestions } from "./use-location-suggestions"

/**
 * Search text for all static options to preload
 */
const STATIC_OPTIONS = [
  {
    display: "Cherry Capital Airport",
    search: "Cherry Capital Airport, MI, 49686, USA",
  },
  {
    display: "Manistee County Blacker Airport",
    search: "Manistee County Blacker Airport, MI, 49660, USA",
  },
  {
    display: "Cowell Family Cancer Center",
    search: "Cancer Center, 217 S Madison St, Traverse City, MI 49684",
  },
] as const

interface Option {
  id: string
  value: string
}

interface OptionGroup {
  items: readonly Option[]
}

/**
 * Generates a stable key for an option so base-ui remounts the item
 * only when its displayed value actually changes (e.g. a live
 * current-location option whose label updates), avoiding remounts on
 * every render.
 */
function getOptionKey(option: Option) {
  return `${option.id}-${option.value}`
}

export function LocationSearch({ map }: InjectMapProps) {
  const { from, to } = appRoute.useSearch({
    select: (s) => ({ from: s.from, to: s.to }),
  })
  const { setFrom, setTo } = useSetAppSearch(map)

  return (
    <>
      <SearchLocation
        place={from}
        set={setFrom}
        popoverClassName="md:w-[var(--anchor-width)] w-[calc(100svw)]"
        className="!rounded-l-full sm:!rounded-none"
      />
      <SearchLocation
        place={to}
        set={setTo}
        popoverClassName="md:w-[var(--anchor-width)] w-[calc(100svw)]"
        className="!rounded-full !rounded-l-none"
      />
    </>
  )
}

function useStaticOptions() {
  const { data, error, isLoading } = useSWR(
    "LOCATION_SEARCH_STATIC_OPTIONS",
    preLoadPlaces,
    {
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const staticOptions: Option[] = useMemo(() => {
    if (!data) return []
    return data
      .filter(
        (item) =>
          item &&
          item.topResult &&
          typeof item.topResult.PlaceId === "string" &&
          typeof item.topResult.Text === "string"
      )
      .map(
        (item) =>
          ({
            id: item!.topResult.PlaceId!,
            value: item!.topResult.Text!,
          }) satisfies Option
      )
  }, [data])

  return { staticOptions, error, isLoading }
}

function useSuggestionOptions(searchValue: string) {
  const { query, isLoading } = useLocationSuggestions(searchValue, 500)
  const { data, error } = query

  const suggestionOptions: Option[] = useMemo(() => {
    if (!data?.Results) return []
    return data.Results.map(
      (r) =>
        ({
          id: r.PlaceId!,
          value: r.Text!,
        }) satisfies Option
    )
  }, [data])

  return { suggestionOptions, error, isLoading }
}

function SearchLocation({
  className,
  popoverClassName,
  place,
  set,
}: {
  className: string
  popoverClassName?: string
  place?: AppPlace
  set: (v: AppPlace) => void
}) {
  const [searchValue, setSearchValue] = React.useState(place?.key ?? "")

  // const prevPlaceKeyRef = useRef<string | undefined>(place?.key)
  useEffect(() => {
    if (place?.key !== searchValue) {
      setSearchValue(place?.key ?? "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place?.key])

  const {
    staticOptions,
    error: staticOptionsError,
    isLoading: areStaticOptionsLoading,
  } = useStaticOptions()
  const {
    suggestionOptions,
    error: suggestionsError,
    isLoading: areSuggestionsLoading,
  } = useSuggestionOptions(searchValue)

  const handleSetValue = useCallback(
    async (option: Option) => {
      setSearchValue(option.value)
      const placeId = option.id
      if (!placeId) return

      const res = await aws.query.getPlace({
        PlaceId: placeId,
      })

      set({
        key: option.value ?? res.Place?.Label ?? "",
        latlng: [
          res.Place?.Geometry?.Point?.[1] ?? 0,
          res.Place?.Geometry?.Point?.[0] ?? 0,
        ],
      })
    },
    [set]
  )

  const optionGroups: readonly OptionGroup[] = useMemo(() => {
    const groups: OptionGroup[] = []
    if (staticOptions.length > 0) {
      groups.push({
        items: staticOptions,
      })
    }
    if (searchValue && suggestionOptions.length > 0) {
      groups.push({
        items: suggestionOptions,
      })
    }
    return groups as readonly OptionGroup[]
  }, [staticOptions, suggestionOptions, searchValue])

  let status: React.ReactNode = "Start typing to see more results near you."
  if (areStaticOptionsLoading || areSuggestionsLoading) {
    status = (
      <span className="grid place-items-center mt-4 mb-4 gap-2">
        <Icons.general.loader className="size-4 animate-spin" />
        Loading destinations...
      </span>
    )
  } else if (staticOptionsError || suggestionsError) {
    status = staticOptionsError?.message || suggestionsError?.message
  } else if (searchValue && suggestionOptions.length === 0) {
    status = `No places found for "${searchValue}".`
  } else if (searchValue && suggestionOptions.length > 0) {
    status = `${suggestionOptions.length} result${suggestionOptions.length === 1 ? "" : "s"} found`
  }

  return (
    <AutocompleteRoot
      // @ts-expect-error - Base UI v1.2.0 expects different type structure
      items={optionGroups}
      value={searchValue}
      defaultValue={searchValue}
      onValueChange={setSearchValue}
      filter={null}
      openOnInputClick={true}
    >
      <div
        data-slot="input-group"
        className={cn(
          "group/input-group border-input dark:bg-input/30 relative flex w-full items-center rounded-md border shadow-xs transition-[color,box-shadow] outline-none",
          "h-9 min-w-0 has-[>textarea]:h-auto",

          // Variants based on alignment.
          "has-[>[data-align=inline-start]]:[&>input]:pl-2",
          "has-[>[data-align=inline-end]]:[&>input]:pr-2",
          "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3",
          "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3",

          // Focus state.
          "has-[[data-slot=input-group-control]:focus-visible]:border-primary has-[[data-slot=input-group-control]:focus-visible]:ring-primary/30 has-[[data-slot=input-group-control]:focus-visible]:ring-2",

          // Error state.
          "has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40",

          "!rounded-none border border-border border-r-transparent bg-background h-full pl-4 pr-4 md:px-4",
          className
        )}
      >
        <AutocompleteInput
          placeholder="Enter an address or place e.g. 1 York St"
          className="bg-transparent focus-visible:ring-transparent focus-visible:border-transparent hover:bg-transparent px-4"
          data-slot="input-group-control"
        />

        <div
          role="group"
          data-slot="input-group-addon"
          data-align={inputAlign}
          className={cn(
            inputGroupAddonVariants({ align: inputAlign }),
            className
          )}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("button")) {
              return
            }
            e.currentTarget.parentElement?.querySelector("input")?.focus()
          }}
        >
          <Icons.input.search className="size-4" />
        </div>
      </div>

      <AutocompletePortal>
        <AutocompletePositioner alignOffset={0}>
          <AutocompletePopup
            className={cn(
              "max-h-[min(var(--available-height),100svh)] sm:max-h-[min(var(--available-height),28rem)]",
              popoverClassName
            )}
            aria-busy={
              areStaticOptionsLoading || areSuggestionsLoading || undefined
            }
          >
            <AutocompleteList>
              {(group: OptionGroup) => (
                <AutocompleteGroup
                  key={group.items[0]?.id ?? "group"}
                  items={group.items}
                >
                  <AutocompleteCollection>
                    {(option: Option) => (
                      <AutocompleteItem
                        key={getOptionKey(option)}
                        value={option}
                        onClick={() => handleSetValue(option)}
                      >
                        <div className="flex w-full flex-col gap-1">
                          <div className="font-medium leading-5">
                            {option.value}
                          </div>
                        </div>
                      </AutocompleteItem>
                    )}
                  </AutocompleteCollection>
                </AutocompleteGroup>
              )}
            </AutocompleteList>
            <AutocompleteStatus>{status}</AutocompleteStatus>
          </AutocompletePopup>
        </AutocompletePositioner>
      </AutocompletePortal>
    </AutocompleteRoot>
  )
}

async function preLoadPlaces() {
  const results = await minDelay(
    Promise.all(
      STATIC_OPTIONS.map(async (o) => {
        const res = await aws.query.suggest({
          Text: o.search,
        })

        return res.Results?.length && res.Results.length >= 1
          ? {
              topResult: res.Results[0],
              search: o,
            }
          : undefined
      })
    ),
    800
  )

  return results
}

const inputAlign = "inline-start" as const

const inputGroupAddonVariants = cva(
  "text-muted-foreground flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium select-none [&>svg:not([class*='size-'])]:size-4 [&>kbd]:rounded-[calc(var(--radius)-5px)] group-data-[disabled=true]/input-group:opacity-50",
  {
    variants: {
      align: {
        "inline-start":
          "order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]",
        "inline-end":
          "order-last pr-3 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]",
        "block-start":
          "order-first w-full justify-start px-3 pt-3 [.border-b]:pb-3 group-has-[>input]/input-group:pt-2.5",
        "block-end":
          "order-last w-full justify-start px-3 pb-3 [.border-t]:pt-3 group-has-[>input]/input-group:pb-2.5",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  }
)
